// Experiment Types

import { getTheme } from "@fluentui/react";
import { AcceptedFeatures } from "./Shared/shared.consts";

const theme = getTheme();

export interface ExperimentParams {
  extensionName: string;
  experimentName: string;
}

export interface ExtensionFolder {
  commitId: string;
  gitObjectType: string;
  isFolder: boolean;
  objectId: string;
  path: string;
  // The path relative to the repo root
  extensionRepoPath: string;
  // If a multifolder extension, this will be the folder after /Extensions/
  rootExtension: string;
  url: string;
  name: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  isParent?: boolean;
}

export interface TenantFilters {
  operator: "IN" | "NOTIN";
  tenantList: string[];
}

export interface ExtensionVersionFilters {
  operator: ">=" | "<=" | "==" | "!=" | "<" | ">";
  version: string;
}

export interface EnvironmentFilters {
  RC?: string[];
  MPAC?: string[];
  Prod?: string[];
}

export interface ExtensionDetailsType {
  displayName: string;
  secretId: string;
  assignmentUnit: string;
  environment: string;
  serviceIdentifier: string;
  serviceGroup: string;
  tenantId: string;
  subscriptionKey: string;
  applicationId: string;
  extensionFilters?: string[];
}

export interface ExperimentConfigType extends Partial<ExtensionDetailsType> {
  analysisTags: string[][];
  featureDescription: string;
  featureKey: string;
  featureName: string;
  isMultiConfig?: boolean;
  extensionFilters?: string[];
  tenantFilters?: TenantFilters;
  extensionVersionFilters?: ExtensionVersionFilters;
  environmentFilters?: EnvironmentFilters;
  variantDetails?: VariantDetail[];
}

export interface ExperimentType extends ExperimentConfigType {
  __filename: string;
  __fullpath: string;
  __extension: string; // Folder extension, in multifolder will be /Extensions/<rootExtension>/<nestedExtension>
  __rootExtension: string; // The root extension folder for multifolder extensions, /Extensions/<rootExtension>/
  lastModifiedBy?: string; // Optional field for last modified by
  lastModifiedDate?: string; // Optional field for last modified date
  pr?: PullRequest; // Optional field for PR URL
  prUrl?: string; // Optional field for PR URL
  isNew?: boolean; // Optional field for new experiments
  enableExtensionPrefix?: boolean; // Optional field for enabling extension prefix
  rolloutPolicies?: ServicePolicyExperimentType[]; // Optional field for rollout policies
  [key: string]: any; // Allow additional properties
}

export interface PipelineConfigurationPr extends PullRequest {
  singleStagePipelineId: string | null;
  unifiedPipelineId: string | null;
  prUrl: string | null;
  __rootExtension: string | null;
}

export interface VariantDetail {
  featureFlags: VariantDetailFeatureFlags;
  isControl: boolean;
  name: string;
}

export interface VariantDetailFeatureFlags {
  [key: string]: boolean | number | string;
}

// Pipeline Types

export interface ApprovalLinkData {
  approvalLink: string | null;
  status: string | null;
}

export interface PipelineDefinition {
  url: string;
  id: number;
  revision: number;
  name: string;
  path: string;
}

export interface TemplateParameters {
  ValidateOnly: string;
  RolloutAndProgressionType: string;
  ConfigSpecPath: string;
  ConfigSpecFileName: string;
  EnableHealthChecks?: boolean;
}

export interface ExtractedArtifactFile {
  path: string;
  filename: string;
  content: string;
  isBinary: boolean;
  contentEncoding: "text" | "base64";
}

export interface PipelineArtifactDownloadResult {
  artifactName: string;
  downloadUrl: string;
  files: ExtractedArtifactFile[];
}

export interface PipelineRunArtifact {
  name: string;
  signedContent?: {
    url?: string;
  };
  resource?: {
    downloadUrl?: string;
  };
}

export interface PipelineRunArtifactsResponse {
  value: PipelineRunArtifact[];
}

export interface PipelineLinks {
  self: { href: string };
  web: { href: string };
}

export enum LinkTypes {
  ApprovalStatus = "approval",
  ApprovalLink = "approvalLink",
  Ev2 = "ev2Portal",
}

export interface RequestedBy {
  uniqueName: string;
  displayName: string;
}

export interface PipelineRunUserData extends PipelineRun {
  finishTime: string;
  startTime: string;
  id: number;
  buildNumber: string;
  definition: PipelineDefinition;
  status: string;
  result: string | null;
  templateParameters: TemplateParameters;
  requestedBy: RequestedBy;
  _links: PipelineLinks;
}

export interface PipelineRun {
  finishedDate: string;
  createdDate: string;
  id: number;
  name: string;
  pipeline: PipelineDefinition;
  state: string;
  result: string | null;
  templateParameters: TemplateParameters;
  url: string;
  _links: PipelineLinks;
}

export type PipelineStatusMap = Record<
  // configFileName
  string,
  // environment (RC, MPAC, PROD) to status counts
  Record<
    string,
    {
      statuses: Ev2ExperimentStage[];
      daysSinceStart: number;
      isLegacy?: boolean;
    } | null
  >
>;

export enum PipelineStatus {
  COMPLETED = "completed",
  SUCCEEDED = "succeeded",
  WARNING = "warning",
  FAILED = "failed",
  CANCELED = "canceled",
  CANCELLED = "cancelled",
  IN_PROGRESS = "inprogress",
  RUNNING = "running",
  PENDING = "pending",
  SKIPPED = "skipped",
  LEGACY = "legacy",
  WAITING = "waiting",
  CANCELLING = "cancelling",
  UNKNOWN = "unknown",
  NOT_AVAILABLE = "not available",
}

export const PipelineStatusColor: Record<string, string> = {
  COMPLETED: theme.palette.green,
  FAILED: theme.palette.redDark,
  CANCELED: theme.palette.neutralSecondary,
  RUNNING: theme.palette.themePrimary,
  PENDING: theme.palette.neutralTertiary,
  WARNING: theme.palette.orangeLighter,
  UNKNOWN: theme.palette.neutralSecondary,
  NOT_AVAILABLE: theme.palette.neutralSecondary,
};

export interface ExtensionPipelineIds {
  singleStagePipelineId: number | null;
  unifiedPipelineId: number | null;
}

// Pull Request Types

export interface CompletionOptions {
  mergeCommitMessage: string;
  deleteSourceBranch: boolean;
  squashMerge: boolean;
  mergeStrategy: string;
  transitionWorkItems: boolean;
}

export interface UserIdentity {
  displayName: string;
  url: string;
  _links: Record<string, any>;
  id: string;
  uniqueName: string;
  imageUrl?: string;
  descriptor?: string;
}

export interface Commit {
  commitId: string;
  url: string;
}

export interface Repository {
  id: string;
  name: string;
  url: string;
  project: {
    id: string;
    name: string;
    description?: string;
    url: string;
    state: string;
    revision: number;
    visibility: string;
    lastUpdateTime: string;
  };
}

export interface Reviewer {
  reviewerUrl: string;
  vote: number;
  hasDeclined: boolean;
  isFlagged: boolean;
  displayName: string;
  url: string;
  _links: Record<string, any>;
  id: string;
  uniqueName: string;
  imageUrl?: string;
  isRequired?: boolean;
}

export interface PullRequest {
  codeReviewId: number;
  completionOptions: CompletionOptions;
  createdBy: UserIdentity;
  creationDate: string;
  description: string;
  isDraft: boolean;
  lastMergeCommit: Commit;
  lastMergeSourceCommit: Commit;
  lastMergeTargetCommit: Commit;
  mergeId: string;
  mergeStatus: string;
  pullRequestId: number;
  repository: Repository;
  reviewers: Reviewer[];
  sourceRefName: string;
  status: string;
  supportsIterations: boolean;
  targetRefName: string;
  title: string;
  url: string;
}

export interface PullRequestChangeItem {
  objectId?: string;
  path: string;
}

export interface PullRequestChange {
  changeType: "add" | "edit" | "delete" | "rename";
  item?: PullRequestChangeItem;
  itemPath?: string;
  newContent?: string;
  contentType?: "rawtext" | "base64encoded";
  sourceServerItem?: string;
}

// Authentication and Deployment Types

interface AccountProperties {
  $type: string;
  $value: string;
}

interface Account {
  Account: AccountProperties;
}

export interface AuthenticatedUser {
  id: string;
  descriptor: string;
  subjectDescriptor: string;
  providerDisplayName: string;
  isActive: boolean;
  properties: Account;
  [key: string]: any; // Allow additional properties
}

export interface LocationServiceData {
  serviceOwner: string;
  defaultAccessMappingMoniker: string;
  lastChangeId: number;
  lastChangeId64: number;
}

export interface ConnectionData {
  authenticatedUser: AuthenticatedUser;
  authorizedUser: AuthenticatedUser;
  deploymentId: string;
  deploymentType: string;
  instanceId: string;
  locationServiceData: LocationServiceData;
}

export interface ServicePolicyExperimentType {
  name: string;
  version: string;
}

export interface ServicePolicy {
  Name: string;
  Version: string;
  AutoApply: boolean;
  IsAzurePolicy: boolean;
}

export interface ServicePolicyTimeOfWeek {
  DayOfWeek: string;
  TimeOfDay: string;
}

export interface ServicePolicyEffectiveTimeRange {
  From: ServicePolicyTimeOfWeek;
  To: ServicePolicyTimeOfWeek;
}

export interface ServicePolicyNoRolloutZone {
  Name: string;
  Locations: string[];
  EffectiveTimeRanges: ServicePolicyEffectiveTimeRange[];
  SubscriptionIds: string[];
}

export interface ServicePolicyNoRollout {
  NoRolloutZones: ServicePolicyNoRolloutZone[];
}

export interface ServicePolicyVersion extends ServicePolicy {
  NoRollout?: ServicePolicyNoRollout;
  RegistrationTime?: string;
  RegisteredBy?: string;
  ContentVersion?: string;
}

// Branch Types

export interface BranchCreator {
  displayName: string;
  url: string;
  _links: Record<string, any>;
  id: string;
  uniqueName: string;
  [key: string]: any; // Allow additional properties like imageUrl, descriptor, etc.
}

export interface AdoBranch {
  creator: BranchCreator;
  name: string;
  objectId: string;
  url: string;
}

// Build Timeline Types

export interface BuildTimelineRecord {
  previousAttempts?: any[];
  id: string;
  parentId?: string;
  type: string;
  name: string;
  refName?: string;
  startTime?: string;
  finishTime?: string;
  currentOperation?: string | null;
  percentComplete?: number | null;
  state: string;
  result?: string | null;
  resultCode?: string | null;
  changeId: number;
  lastModified: string;
  workerName?: string;
  queueId?: number;
  order?: number;
  details?: {
    id: string;
    changeId: number;
    url: string;
  } | null;
  errorCount?: number;
  warningCount?: number;
  url?: string | null;
  log?: {
    id: number;
    type: string;
    url: string;
  };
  task?: {
    id: string;
    name: string;
    version: string;
  };
  attempt?: number;
  identifier?: string | null;
  [key: string]: any; // Allow additional properties
}

export interface BuildTimeline {
  changeId: number;
  id: string;
  lastChangedBy: string;
  lastChangedOn: string;
  records: BuildTimelineRecord[];
  url: string;
}

// Rollout Template Types

export interface RolloutTemplateParameter {
  name: string;
  displayName: string;
  type: string;
  default?: any;
  values?: string[];
  [key: string]: any; // Allow additional properties
}

export interface RolloutTemplateRepository {
  repository: string;
  type: string;
  name: string;
  ref: string;
}

export interface RolloutTemplateResources {
  repositories: RolloutTemplateRepository[];
  [key: string]: any; // Allow additional resource types
}

export interface RolloutTemplate {
  extends: string;
  parameters: RolloutTemplateParameter[];
  resources: RolloutTemplateResources;
  trigger: string;
  variables: Record<string, any>;
  __filename: string;
  __fullpath: string;
  __raw: string;
  [key: string]: any; // Allow additional YAML properties
}

export interface RolloutDetailsType {
  ConfigSpecFileName: string;
  ConfigSpecPath: string;
  RolloutAndProgressionType: RolloutAndProgressionType;
  ValidateOnly: boolean;
  [key: string]: any; // Allow additional rollout properties
}

export enum RolloutAndProgressionType {
  RC_SDP = "RC_SDP",
  RC_SDP_MANUAL = "RC_SDP_Manual",
  RC_HOTFIX = "RC_Hotfix",
  RC_ROLLBACK = "RC_Rollback",
  MPAC_SDP = "MPAC_SDP",
  MPAC_SDP_MANUAL = "MPAC_SDP_Manual",
  MPAC_HOTFIX = "MPAC_Hotfix",
  MPAC_ROLLBACK = "MPAC_Rollback",
  PROD_SDP = "PROD_SDP",
  PROD_SDP_MANUAL = "PROD_SDP_Manual",
  PROD_HOTFIX = "PROD_Hotfix",
  PROD_ROLLBACK = "PROD_Rollback",
}

// EV2 Types

export interface Ev2ExperimentError {
  code: string;
  reason: string;
}

export interface Ev2ExperimentLinks {
  experiment: string;
  analysis: string;
}

export enum RolloutActionType {
  RolloutExperiment = "extension/RolloutExperiment",
  ExperimentWaitTime = "wait/experimentWaitTime",
  MonitorExperiment = "mdmHealthCheck/MonitorExperiment",
}

export type RolloutAction =
  | "extension/RolloutExperiment"
  | "wait/experimentWaitTime"
  | "mdmHealthCheck/MonitorExperiment";

export interface Ev2ExperimentStage {
  stageName: string;
  stamp: number;
  status: string;
  startTime: string;
  endTime: string;
  rolloutAction: RolloutAction;
  links: Ev2ExperimentLinks;
  error: Ev2ExperimentError;
  healthCheck?: boolean | string;
  isLegacy?: boolean;
  environment?: string;
}

export interface Ev2ExperimentStatusResults {
  [rolloutId: string]: Ev2ExperimentStage[];
}

// File Commit Types

export interface FileCommitAuthor {
  name: string;
  email: string;
  date: string;
}

export interface FileCommitCommitter {
  name: string;
  email: string;
  date: string;
}

export interface FileCommit {
  commitId: string;
  author: FileCommitAuthor;
  committer: FileCommitCommitter;
  comment: string;
  changeCounts?: {
    Add: number;
    Edit: number;
    Delete: number;
  };
  url: string;
  remoteUrl: string;
}

// Feature Types

export interface FeatureConfig {
  key: string;
  type: "boolean" | "string" | "number";
}

export type FeatureKeyType = keyof typeof AcceptedFeatures;
